import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { PetEntity } from '../logic/EntityManager';
import StateObserver, { now } from 'src/StateObserver';
import { PetSpeciesID, PetState } from 'src/replicant/pet';
import { getPet } from '.';
import { MovieClip } from '@play-co/timestep-core/ui';
import { getSceneManager } from 'src/lib/utils';
import { PET_CRATE_HEIGHT_RATIO } from '../position/position.config';
import uiConfig from 'src/lib/ui/config';

/**
 * Create a pet entity for UI purposes.
 * @param state - The state of the pet. Will determine the animation. Default is 'normal'.
 * @param species - The species of the pet. Default is the current pet species.
 * @returns The vanity pet.
 */
export function createUIPet(
  state: PetState = getPet().state,
  species: PetSpeciesID = getPet().species ?? 'dog',
) {
  const animationName = state === 'sleeping' ? 'sleep' : state;
  const pet = new PetEntity(`vanity${now()}`, species);
  const petAnimations = petAnimationNames[species][animationName];
  pet.setAnimation(
    typeof petAnimations === 'string' ? petAnimations : petAnimations.idle,
  );
  pet.updateOpts({
    scale: 0.7,
    anchorX: 0.5 * pet.style.width,
    anchorY: 0.5 * pet.style.height,
    offsetY: -0.5 * pet.style.height,
  });

  return pet;
}

/**
 * Create a pet crate for UI purposes.
 * @returns
 */
export function createUIPetCrate() {
  const petCrate = new MovieClip({
    url: 'assets/tutorial/crate',
    defaultAnimation: 'pet_crate_idle',
  });
  return petCrate;
}

/**
 * Holds the reference to the pet crate created for the new tutorial
 */
let homePetCrate: MovieClip | null = null;

/**
 * Create the crate for the user to open and reveal their first pet.
 *
 * @returns The existing or newly created initial pet crate.
 */
export function getHomePetCrate() {
  // Return the existing pet crate if already created
  if (homePetCrate) return homePetCrate;

  // Check if the living room screen exists before creating the pet crate
  const livingRoomScreen = getSceneManager()?.getScene('livingRoom');
  if (!livingRoomScreen) {
    throw new Error('Living room screen not found when creating pet crate.');
  }
  const livingRoomView = livingRoomScreen.getView();

  // Use the base height while detached because ResponsiveScene will scale child Y on attachment.
  const { height } = StateObserver.getState().ui.screenSize;
  const petCrateLayoutHeight = livingRoomView.getSuperview()
    ? height
    : uiConfig.height;

  homePetCrate = createUIPetCrate();
  homePetCrate.updateOpts({
    superview: livingRoomView,
    x: 365,
    y: petCrateLayoutHeight * PET_CRATE_HEIGHT_RATIO,
    zIndex: 1,
    offsetX: 170,
    offsetY: 124,
    centerOnOrigin: true,
  });

  return homePetCrate;
}
